package scenes

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.spring
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.focusable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.key.*
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import domain.cards.CardDefinition
import domain.cards.CardRarity
import kotlinx.coroutines.delay
import rendering.fontMono

private fun CardRarity.toColor(): Color = when (this) {
    CardRarity.COMMON    -> Color(0xFF888888)
    CardRarity.RARE      -> Color(0xFF2979FF)
    CardRarity.EPIC      -> Color(0xFFAA00FF)
    CardRarity.LEGENDARY -> Color(0xFFFFD600)
}

/**
 * Full-screen card pick overlay shown after completing a story room.
 *
 * Shows up to 3 card options. If the player cannot afford any card,
 * shows a "BANKRUPT" screen that auto-dismisses after 2.5 seconds.
 *
 * @param cards               Card options to display (1–3 cards).
 * @param coins               Current player coin balance.
 * @param onCardPicked        Called when the player selects an affordable card.
 * @param onBankruptDismissed Called when the bankrupt screen auto-dismisses.
 */
@Composable
fun CardPickScene(
    cards: List<CardDefinition>,
    coins: Int,
    onCardPicked: (CardDefinition) -> Unit,
    onBankruptDismissed: () -> Unit,
) {
    val focusRequester = remember { FocusRequester() }
    var selectedIndex by remember { mutableStateOf(0) }

    val monoStyle = TextStyle(fontFamily = fontMono(), letterSpacing = 0.05.sp)

    // Determine if player is bankrupt (can't afford any card)
    val isBankrupt = cards.isNotEmpty() && cards.all { coins < it.coinCost }

    LaunchedEffect(Unit) { focusRequester.requestFocus() }

    // Auto-dismiss bankrupt screen after 2.5 seconds
    LaunchedEffect(isBankrupt) {
        if (isBankrupt) {
            delay(2500L)
            onBankruptDismissed()
        }
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color(0xCC000000))
            .focusRequester(focusRequester)
            .focusable()
            .onKeyEvent { event ->
                if (event.type != KeyEventType.KeyDown) return@onKeyEvent false
                if (isBankrupt) return@onKeyEvent false
                when (event.key) {
                    Key.DirectionLeft -> {
                        selectedIndex = (selectedIndex - 1 + cards.size) % cards.size
                        true
                    }
                    Key.DirectionRight -> {
                        selectedIndex = (selectedIndex + 1) % cards.size
                        true
                    }
                    Key.Enter -> {
                        val card = cards.getOrNull(selectedIndex)
                        if (card != null && coins >= card.coinCost) {
                            onCardPicked(card)
                        }
                        true
                    }
                    else -> false
                }
            },
        contentAlignment = Alignment.Center,
    ) {
        if (isBankrupt) {
            // Bankrupt screen
            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Center,
            ) {
                Text(
                    text = "BANKRUPT",
                    style = monoStyle.copy(
                        fontSize = 36.sp,
                        color = Color(0xFFFF1744),
                        letterSpacing = 0.2.sp,
                    ),
                    textAlign = TextAlign.Center,
                )
                Spacer(modifier = Modifier.height(12.dp))
                Text(
                    text = "You cannot afford any card.",
                    style = monoStyle.copy(fontSize = 12.sp, color = Color(0xFF888888)),
                    textAlign = TextAlign.Center,
                )
                Spacer(modifier = Modifier.height(8.dp))
                Text(
                    text = "Continuing...",
                    style = monoStyle.copy(fontSize = 10.sp, color = Color(0xFF444466)),
                    textAlign = TextAlign.Center,
                )
            }
        } else {
            // Card picker
            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                Text(
                    text = "CHOOSE A CARD",
                    style = monoStyle.copy(
                        fontSize = 14.sp,
                        color = Color(0xFF888888),
                        letterSpacing = 0.2.sp,
                    ),
                )
                Spacer(modifier = Modifier.height(8.dp))
                Text(
                    text = "Coins: $coins",
                    style = monoStyle.copy(fontSize = 11.sp, color = Color(0xFFFFD600)),
                )
                Spacer(modifier = Modifier.height(24.dp))

                Row(
                    horizontalArrangement = Arrangement.spacedBy(16.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    cards.forEachIndexed { index, card ->
                        val isSelected = index == selectedIndex
                        val canAfford = coins >= card.coinCost
                        val liftY by animateFloatAsState(
                            targetValue = if (isSelected) -16f else 0f,
                            animationSpec = spring(dampingRatio = 0.5f, stiffness = 300f),
                        )
                        val rarityColor = card.rarity.toColor()
                        val cardAlpha = if (canAfford) 1f else 0.4f

                        Box(
                            modifier = Modifier
                                .width(160.dp)
                                .height(260.dp)
                                .graphicsLayer {
                                    translationY = liftY
                                    alpha = cardAlpha
                                    scaleX = if (isSelected) 1.04f else 1f
                                    scaleY = if (isSelected) 1.04f else 1f
                                }
                                .drawBehind {
                                    drawCircle(
                                        color = rarityColor.copy(alpha = if (isSelected) 0.18f else 0.08f),
                                        radius = size.maxDimension * 0.65f,
                                    )
                                }
                                .background(Color(0xFF0A0A18))
                                .border(
                                    width = if (isSelected) 2.dp else 1.dp,
                                    color = if (isSelected) rarityColor else rarityColor.copy(alpha = 0.35f),
                                ),
                            contentAlignment = Alignment.TopStart,
                        ) {
                            Column(modifier = Modifier.fillMaxSize()) {
                                // Top accent bar
                                Box(modifier = Modifier.fillMaxWidth().height(2.dp).background(rarityColor.copy(alpha = if (isSelected) 1f else 0.5f)))

                                Column(modifier = Modifier.fillMaxSize().padding(14.dp)) {
                                    // Rarity badge
                                    Box(
                                        modifier = Modifier
                                            .background(rarityColor.copy(alpha = 0.2f), shape = RoundedCornerShape(3.dp))
                                            .padding(horizontal = 6.dp, vertical = 2.dp),
                                    ) {
                                        Text(
                                            text = card.rarity.name,
                                            style = monoStyle.copy(fontSize = 8.sp, color = rarityColor, letterSpacing = 0.2.sp),
                                        )
                                    }
                                    Spacer(modifier = Modifier.height(8.dp))

                                    // Card name
                                    Text(
                                        text = card.name,
                                        style = monoStyle.copy(fontSize = 13.sp, color = Color(0xFFFFFFFF)),
                                    )
                                    Spacer(modifier = Modifier.height(6.dp))

                                    // Description
                                    Text(
                                        text = card.description,
                                        style = monoStyle.copy(fontSize = 9.sp, color = Color(0xFF888888)),
                                    )

                                    // Flavour text
                                    card.flavourText?.let { flavour ->
                                        Spacer(modifier = Modifier.height(6.dp))
                                        Text(
                                            text = "\"$flavour\"",
                                            style = monoStyle.copy(fontSize = 8.sp, color = Color(0xFF555577)),
                                        )
                                    }

                                    Spacer(modifier = Modifier.weight(1f))

                                    // Coin cost box
                                    Box(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .background(Color(0xFF0D0D1A))
                                            .border(1.dp, if (canAfford) Color(0xFFFFD600).copy(alpha = 0.6f) else Color(0xFFFF1744).copy(alpha = 0.6f))
                                            .padding(horizontal = 8.dp, vertical = 6.dp),
                                        contentAlignment = Alignment.Center,
                                    ) {
                                        Row(
                                            verticalAlignment = Alignment.CenterVertically,
                                            horizontalArrangement = Arrangement.spacedBy(4.dp),
                                        ) {
                                            Text(
                                                text = "⬡",
                                                style = monoStyle.copy(fontSize = 12.sp, color = if (canAfford) Color(0xFFFFD600) else Color(0xFFFF1744)),
                                            )
                                            Text(
                                                text = "${card.coinCost}",
                                                style = monoStyle.copy(fontSize = 14.sp, color = if (canAfford) Color(0xFFFFD600) else Color(0xFFFF1744)),
                                            )
                                            if (!canAfford) {
                                                Text(
                                                    text = "✗",
                                                    style = monoStyle.copy(fontSize = 10.sp, color = Color(0xFFFF1744)),
                                                )
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                Spacer(modifier = Modifier.height(16.dp))
                Text(
                    text = "← → to select  •  Enter to pick",
                    style = monoStyle.copy(fontSize = 9.sp, color = Color(0xFF444466)),
                    textAlign = TextAlign.Center,
                )
            }
        }
    }
}
